import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { api } from '../services';
import { ENDPOINTS } from '../services/endpoints';
import { getApiErrorMessage } from '../shared/errorUtils';

// Async thunk for creating alert
export const createAlert = createAsyncThunk(
  'alerts/createAlert',
  async (alertData, { rejectWithValue }) => {
    try {
      const response = await api.post(ENDPOINTS.CREATE_ALERT, alertData);
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'projectAlerts.errors.createFailed'));
    }
  }
);

// Async thunk for updating alert
export const updateAlert = createAsyncThunk(
  'alerts/updateAlert',
  async ({ alertId, alertData }, { rejectWithValue }) => {
    try {
      const response = await api.put(ENDPOINTS.UPDATE_ALERT(alertId), alertData);
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'projectAlerts.errors.updateFailed'));
    }
  }
);

// Async thunk for deleting alert
export const deleteAlert = createAsyncThunk(
  'alerts/deleteAlert',
  async (alertId, { rejectWithValue }) => {
    try {
      await api.delete(ENDPOINTS.DELETE_ALERT(alertId));
      return alertId;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'projectAlerts.errors.deleteFailed'));
    }
  }
);

// Async thunk for fetching alerts
export const fetchAlerts = createAsyncThunk(
  'alerts/fetchAlerts',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get(ENDPOINTS.ALERTS);
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'projectAlerts.errors.fetchFailed'));
    }
  }
);

// Async thunk for triggering alerts
export const triggerAlerts = createAsyncThunk(
  'alerts/triggerAlerts',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.post(ENDPOINTS.TRIGGER_ALERTS);
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'projectAlerts.errors.triggerFailed'));
    }
  }
);

const initialState = {
  alerts: {},
  loading: false,
  error: null,
  createLoading: false,
  updateLoading: false,
  deleteLoading: false,
  triggerLoading: false,
};

const alertSlice = createSlice({
  name: 'alerts',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    optimisticToggleAlert: (state, action) => {
      const { alertId, enabled } = action.payload;
      // Update the alert status optimistically in the state
      Object.keys(state.alerts).forEach(projectName => {
        if (Array.isArray(state.alerts[projectName])) {
          const alertIndex = state.alerts[projectName].findIndex(alert => alert.id === alertId);
          if (alertIndex !== -1) {
            state.alerts[projectName][alertIndex].status = enabled ? 'active' : 'inactive';
          }
        }
      });
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createAlert.pending, (state) => {
        state.createLoading = true;
        state.error = null;
      })
      .addCase(createAlert.fulfilled, (state) => {
        state.createLoading = false;
      })
      .addCase(createAlert.rejected, (state, action) => {
        state.createLoading = false;
        state.error = action.payload;
      })
      .addCase(updateAlert.pending, (state) => {
        state.updateLoading = true;
        state.error = null;
      })
      .addCase(updateAlert.fulfilled, (state) => {
        state.updateLoading = false;
      })
      .addCase(updateAlert.rejected, (state, action) => {
        state.updateLoading = false;
        state.error = action.payload;
      })
      .addCase(deleteAlert.pending, (state) => {
        state.deleteLoading = true;
        state.error = null;
      })
      .addCase(deleteAlert.fulfilled, (state) => {
        state.deleteLoading = false;
      })
      .addCase(deleteAlert.rejected, (state, action) => {
        state.deleteLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchAlerts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAlerts.fulfilled, (state, action) => {
        state.loading = false;
        state.alerts = action.payload.data || {};
      })
      .addCase(fetchAlerts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(triggerAlerts.pending, (state) => {
        state.triggerLoading = true;
        state.error = null;
      })
      .addCase(triggerAlerts.fulfilled, (state) => {
        state.triggerLoading = false;
      })
      .addCase(triggerAlerts.rejected, (state, action) => {
        state.triggerLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, optimisticToggleAlert } = alertSlice.actions;
export default alertSlice.reducer;